import { useEffect, useRef, useState } from "react";
import styles from "./Marquee.module.css";

/**
 * Seamless horizontal auto-scroll for a row of cards.
 *
 * Accessibility notes — auto-moving content is a WCAG 2.2.2 concern,
 * so this deliberately:
 *   - pauses on hover AND on keyboard focus anywhere inside
 *   - exposes a visible pause/play control
 *   - falls back to a plain static grid under prefers-reduced-motion,
 *     so nothing moves and no content is duplicated
 *   - marks the duplicated track aria-hidden and removes it from the
 *     tab order, so screen readers and keyboards see each item once
 */
export default function Marquee({
  children,
  speed = 46, // seconds for one full loop; higher = slower
  reverse = false,
  /* How many times the children repeat *within* one set. The loop
     translates by exactly -50% (one whole set), so a set narrower
     than the viewport would leave a visible gap — short lists need
     repeat >= 2 to stay seamless on wide screens. */
  repeat = 1,
  className = "",
  ariaLabel,
}) {
  const [reduced, setReduced] = useState(false);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  /* aria-hidden alone doesn't stop Tab reaching links inside the
     duplicated content, so every focusable node under any aria-hidden
     container is explicitly removed from the tab order too. */
  useEffect(() => {
    if (reduced || !trackRef.current) return;
    trackRef.current
      .querySelectorAll('[aria-hidden="true"] a, [aria-hidden="true"] button')
      .forEach((el) => el.setAttribute("tabindex", "-1"));
  }, [reduced, children, repeat]);

  if (reduced) {
    return (
      <div className={`${styles.static} ${className}`} aria-label={ariaLabel}>
        {children}
      </div>
    );
  }

  return (
    <div className={`${styles.wrap} ${className}`}>
      <div
        className={styles.viewport}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
        }}
      >
        <div
          ref={trackRef}
          className={`${styles.track} ${paused ? styles.paused : ""} ${
            reverse ? styles.reverse : ""
          }`}
          style={{ "--speed": `${speed}s` }}
        >
          <div className={styles.set} aria-label={ariaLabel}>
            {/* only the first pass is exposed to assistive tech —
                later passes inside the set are visual padding */}
            {Array.from({ length: repeat }, (_, r) => (
              <div
                key={r}
                className={styles.pass}
                aria-hidden={r > 0 ? "true" : undefined}
              >
                {children}
              </div>
            ))}
          </div>
          {/* visual duplicate that makes the loop seamless */}
          <div className={styles.set} aria-hidden="true">
            {Array.from({ length: repeat }, (_, r) => (
              <div key={r} className={styles.pass} aria-hidden="true">
                {children}
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        className={styles.toggle}
        onClick={() => setPaused((p) => !p)}
        aria-pressed={paused}
      >
        {paused ? "Resume sliding" : "Pause sliding"}
      </button>
    </div>
  );
}
